import React from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { 
  Database, 
  ShoppingCart, 
  LineChart, 
  Users, 
  CreditCard, 
  MessageSquare,
  Globe,
  Mail,
  BarChart3,
  Zap
} from 'lucide-react'

interface DataSourceType {
  id: string
  name: string
  description: string
  category: 'E-commerce' | 'Marketing & Analytics' | 'CRM' | 'Finance' | 'Database' | 'Communication'
  tier: 1 | 2 | 3
  icon: React.ComponentType<{ className?: string }>
  popular?: boolean
  comingSoon?: boolean
}

const dataSourceTypes: DataSourceType[] = [
  // Tier 1: E-commerce Platforms
  {
    id: 'firestore',
    name: 'Firestore',
    description: 'Connect to your Firebase/Firestore database for real-time data analysis',
    category: 'Database',
    tier: 1,
    icon: Database,
    popular: true
  },
  {
    id: 'shopify',
    name: 'Shopify',
    description: 'Sales analytics, product performance, customer behavior',
    category: 'E-commerce',
    tier: 1,
    icon: ShoppingCart,
    popular: true,
    comingSoon: true
  },
  {
    id: 'woocommerce',
    name: 'WooCommerce',
    description: 'WordPress-based e-commerce analytics',
    category: 'E-commerce',
    tier: 1,
    icon: ShoppingCart,
    comingSoon: true
  },
  {
    id: 'magento',
    name: 'Magento Commerce',
    description: 'Enterprise e-commerce analytics',
    category: 'E-commerce',
    tier: 1,
    icon: ShoppingCart,
    comingSoon: true
  },
  {
    id: 'bigcommerce',
    name: 'BigCommerce',
    description: 'Multi-channel commerce analytics',
    category: 'E-commerce',
    tier: 1,
    icon: ShoppingCart,
    comingSoon: true
  },

  // Tier 1: Marketing & Analytics
  {
    id: 'google-analytics',
    name: 'Google Analytics 4',
    description: 'Website traffic, user behavior, conversion tracking',
    category: 'Marketing & Analytics',
    tier: 1,
    icon: BarChart3,
    popular: true
  },
  {
    id: 'facebook-ads',
    name: 'Facebook Ads',
    description: 'Social media advertising performance',
    category: 'Marketing & Analytics',
    tier: 1,
    icon: LineChart,
    comingSoon: true
  },
  {
    id: 'google-ads',
    name: 'Google Ads',
    description: 'Search advertising analytics',
    category: 'Marketing & Analytics',
    tier: 1,
    icon: LineChart,
    comingSoon: true
  },
  {
    id: 'mailchimp',
    name: 'Mailchimp',
    description: 'Email marketing performance',
    category: 'Marketing & Analytics',
    tier: 1,
    icon: Mail,
    comingSoon: true
  },

  // Tier 2: CRM
  {
    id: 'salesforce',
    name: 'Salesforce',
    description: 'Sales pipeline, lead tracking, customer management',
    category: 'CRM',
    tier: 2,
    icon: Users,
    comingSoon: true
  },
  {
    id: 'hubspot',
    name: 'HubSpot',
    description: 'Inbound marketing, sales automation',
    category: 'CRM',
    tier: 2,
    icon: Users,
    comingSoon: true
  },
  {
    id: 'pipedrive',
    name: 'Pipedrive',
    description: 'Sales pipeline management',
    category: 'CRM',
    tier: 2,
    icon: Users,
    comingSoon: true
  },

  // Tier 2: Finance
  {
    id: 'stripe',
    name: 'Stripe',
    description: 'Payment processing analytics, subscription metrics',
    category: 'Finance',
    tier: 2,
    icon: CreditCard,
    comingSoon: true
  },
  {
    id: 'paypal',
    name: 'PayPal',
    description: 'Payment transaction analytics',
    category: 'Finance',
    tier: 2,
    icon: CreditCard,
    comingSoon: true
  },

  // Tier 3: Communication
  {
    id: 'slack',
    name: 'Slack',
    description: 'Team communication patterns and analytics',
    category: 'Communication',
    tier: 3,
    icon: MessageSquare,
    comingSoon: true
  }
]

interface DataSourceSelectionProps {
  onSelectDataSource: (dataSourceId: string) => void
}

const DataSourceSelection: React.FC<DataSourceSelectionProps> = ({ onSelectDataSource }) => {
  const categories = Array.from(new Set(dataSourceTypes.map(ds => ds.category)))
  
  const getFilteredDataSources = (category: string) => {
    return dataSourceTypes.filter(ds => ds.category === category)
  }

  const popularDataSources = dataSourceTypes.filter(ds => ds.popular)

  return (
    <div className="space-y-8">
      {/* Popular Sources */}
      <div>
        <div className="flex items-center gap-2 mb-6">
          <Zap className="h-5 w-5 text-orange-500" />
          <h2 className="text-xl font-semibold text-foreground">Popular Data Sources</h2>
        </div>
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {popularDataSources.map((dataSource) => {
            const IconComponent = dataSource.icon
            return (
              <Card key={dataSource.id} className="relative cursor-pointer hover:shadow-md transition-shadow">
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center">
                        <IconComponent className="h-5 w-5 text-primary" />
                      </div>
                      <div>
                        <CardTitle className="text-base font-medium">{dataSource.name}</CardTitle>
                        <div className="flex items-center gap-2 mt-1">
                          <Badge variant="secondary" className="text-xs">
                            Tier {dataSource.tier}
                          </Badge>
                          {dataSource.popular && (
                            <Badge variant="default" className="text-xs bg-orange-500">
                              Popular
                            </Badge>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-muted-foreground mb-4">
                    {dataSource.description}
                  </p>
                  <Button 
                    className="w-full" 
                    onClick={() => onSelectDataSource(dataSource.id)}
                    disabled={dataSource.comingSoon}
                  >
                    {dataSource.comingSoon ? 'Coming Soon' : 'Connect'}
                  </Button>
                </CardContent>
              </Card>
            )
          })}
        </div>
      </div>

      {/* All Categories */}
      {categories.map((category) => (
        <div key={category}>
          <h2 className="text-xl font-semibold text-foreground mb-6">{category}</h2>
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {getFilteredDataSources(category).map((dataSource) => {
              const IconComponent = dataSource.icon
              return (
                <Card key={dataSource.id} className="relative cursor-pointer hover:shadow-md transition-shadow">
                  <CardHeader className="pb-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center">
                          <IconComponent className="h-5 w-5 text-primary" />
                        </div>
                        <div>
                          <CardTitle className="text-base font-medium">{dataSource.name}</CardTitle>
                          <div className="flex items-center gap-2 mt-1">
                            <Badge variant="secondary" className="text-xs">
                              Tier {dataSource.tier}
                            </Badge>
                            {dataSource.popular && (
                              <Badge variant="default" className="text-xs bg-orange-500">
                                Popular
                              </Badge>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-muted-foreground mb-4">
                      {dataSource.description}
                    </p>
                    <Button 
                      className="w-full" 
                      onClick={() => onSelectDataSource(dataSource.id)}
                      disabled={dataSource.comingSoon}
                    >
                      {dataSource.comingSoon ? 'Coming Soon' : 'Connect'}
                    </Button>
                  </CardContent>
                </Card>
              )
            })}
          </div>
        </div>
      ))}
    </div>
  )
}

export default DataSourceSelection 